import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetWorkItemQuery } from "../../store/services/workItemService";
import { useGetCalendarEventByIdQuery } from "../../store/ms-services/calendarApi";
import { useUpdateNextActionMutation } from "../../store/services/nextActionService";
import {
  useSetWorkItemFavoriteMutation,
  WorkItemFavoriteTypeEnum,
} from "../../store/services/workItemService";
import ActivityPanelHeader from "./components/ActivityPanelHeader";
import ActivityPanelFooter from "./components/ActivityPanelFooter";
import ActivityInfoBar from "./components/ActivityInfoBar";
import RescheduleCard from "./components/RescheduleCard";
import CustomerCallButton from "./components/CustomerCallButton";
import CustomerInfoCard from "../activity-dialog/CustomerInfoCard";
import MeetingCard from "../meeting-card/MeetingCard";
import CalendarEventError from "../calendar-event-error/CalendarEventError";
import CompleteActionDialog from "../activity-complete-dialog/CompleteActionDialog";
import VisualActionDialog from "../next-action/VisualActionDialog";
import MoveWorkItemDialog from "../activity-move-dialog/MoveWorkItemDialog";
import AssignWorkItemDialog from "../dialogs/AssignWorkItemDialog";

const PANEL_WIDTH = 650;
const MINIMIZED_WIDTH = 280;
const MOBILE_MINIMIZED_WIDTH = 160;

import { ActivityPanelMode } from "../../store/slices/activityPanelSlice";

interface ActivityPanelProps {
  activityId: number;
  minimized: boolean;
  isFocused?: boolean;
  initialMode?: ActivityPanelMode;
  onClose: () => void;
  onToggleMinimize: () => void;
  onFocus?: () => void;
  panelOffset?: number;
}

const ActivityPanel = ({
  activityId,
  minimized,
  isFocused = false,
  initialMode,
  onClose,
  onToggleMinimize,
  onFocus,
  panelOffset = 24,
}: ActivityPanelProps) => {
  const navigate = useNavigate();
  const [showCompleteDialog, setShowCompleteDialog] = useState(
    initialMode === "complete",
  );
  const [showNextActionDialog, setShowNextActionDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  // Draggable — use ref for DOM manipulation during drag, state only for final pos
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const panelEl = panelRef.current;
    if (!panelEl) return;
    const rect = panelEl.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left,
      origY: rect.top,
    };
    // Apply direct style for smooth dragging
    panelEl.style.transition = "none";
    const handleDragMove = (ev: MouseEvent) => {
      if (!dragRef.current || !panelEl) return;
      const x = dragRef.current.origX + (ev.clientX - dragRef.current.startX);
      const y = dragRef.current.origY + (ev.clientY - dragRef.current.startY);
      panelEl.style.left = `${x}px`;
      panelEl.style.top = `${y}px`;
      panelEl.style.right = "auto";
      panelEl.style.bottom = "auto";
    };
    const handleDragEnd = (ev: MouseEvent) => {
      if (dragRef.current && panelEl) {
        const x = dragRef.current.origX + (ev.clientX - dragRef.current.startX);
        const y = dragRef.current.origY + (ev.clientY - dragRef.current.startY);
        panelEl.style.transition = "";
        setDragPos({ x, y });
      }
      dragRef.current = null;
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
    };
    window.addEventListener("mousemove", handleDragMove);
    window.addEventListener("mouseup", handleDragEnd);
  }, []);

  const handleResetPosition = useCallback(() => {
    setDragPos(null);
    if (panelRef.current) {
      panelRef.current.style.left = "";
      panelRef.current.style.top = "";
      panelRef.current.style.right = "";
      panelRef.current.style.bottom = "";
    }
  }, []);

  // Reset drag position when minimized
  useEffect(() => {
    if (minimized) handleResetPosition();
  }, [minimized, handleResetPosition]);

  const {
    data: activity,
    isLoading,
    isFetching,
    error,
  } = useGetWorkItemQuery(activityId ? { workItemId: activityId } : skipToken);

  const isRefresh = isFetching && !isLoading;

  const hasExternalId = Boolean(activity?.ExternalId);
  const {
    data: calendarEvent,
    isLoading: isCalendarLoading,
    error: calendarError,
  } = useGetCalendarEventByIdQuery(activity?.ExternalId || "", {
    skip: !hasExternalId,
  });

  const isCalendarEventNotFound =
    calendarError && "status" in calendarError && calendarError.status === 404;

  const shouldShowCalendarContent =
    hasExternalId &&
    (calendarEvent || isCalendarLoading || isCalendarEventNotFound);

  const [setFavorite] = useSetWorkItemFavoriteMutation();
  const [updateWorkItem] = useUpdateNextActionMutation();

  const handleToggleFavorite = () => {
    if (!activity?.Id) return;
    setFavorite({
      workItemId: activity.Id,
      type:
        activity.FavoriteType === WorkItemFavoriteTypeEnum.Favorite
          ? WorkItemFavoriteTypeEnum.NotSet
          : WorkItemFavoriteTypeEnum.Favorite,
    });
  };

  const handleCustomerClick = () => {
    if (activity?.Customer?.Id) {
      navigate(`/customer/${activity.Customer.Id}`);
    }
  };

  const handleUpdateDescription = async (newDescription: string) => {
    if (!activity) throw new Error("Ingen aktivitet hittades");
    await updateWorkItem({
      customerNo: activity.Customer.Id,
      request: {
        scheduledDate: activity.ScheduledTo,
        title: activity.Name,
        description: newDescription,
        teamId: activity.Team?.Id,
        assignedToId: activity.AssignedTo?.Id,
        externalId: activity.ExternalId,
        workNo: activity.WorkId ?? undefined,
        type: activity.Type,
        workItemNo: activity.Id,
      },
    }).unwrap();
  };

  const handleFinishClicked = () => setShowCompleteDialog(true);

  const handleCompleted = () => {
    setShowCompleteDialog(false);
    setShowMoveDialog(false);
    setShowAssignDialog(false);
    onClose();
  };

  const handleCompleteWithNextAction = () => {
    setShowCompleteDialog(false);
    setShowNextActionDialog(true);
  };

  const handleNextActionClose = () => {
    setShowNextActionDialog(false);
    onClose();
  };

  return (
    <>
      <Paper
        ref={panelRef}
        data-panel
        elevation={isFocused ? 16 : 4}
        onMouseDown={onFocus}
        sx={(theme) => {
          const isDragged = dragPos && !minimized;
          return {
            position: "fixed",
            ...(isDragged
              ? {
                  left: dragPos.x,
                  top: dragPos.y,
                  bottom: "auto",
                  right: "auto",
                }
              : { bottom: 0, right: panelOffset }),
            width: minimized ? MINIMIZED_WIDTH : PANEL_WIDTH,
            height: minimized ? "auto" : "50vh",
            zIndex: isFocused
              ? theme.zIndex.drawer + 1
              : theme.zIndex.drawer - 1,
            borderRadius: "12px 12px 0 0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: "all 0.3s ease",
            outline: isFocused
              ? `2px solid ${theme.palette.primary.main}`
              : "2px solid transparent",
            opacity: isFocused || minimized ? 1 : 0.85,
            [theme.breakpoints.down("md")]: minimized
              ? {
                  width: MOBILE_MINIMIZED_WIDTH,
                  right: panelOffset,
                  borderRadius: "8px 8px 0 0",
                }
              : {
                  width: "100%",
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  height: "100dvh",
                  borderRadius: 0,
                  display: isFocused ? "flex" : "none",
                },
          };
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          onMouseDown={!minimized ? handleDragStart : undefined}
          onDoubleClick={handleResetPosition}
          style={{ cursor: !minimized ? "grab" : undefined }}
        >
          <ActivityPanelHeader
            activity={activity}
            activityId={activityId}
            isRefreshing={isRefresh}
            minimized={minimized}
            onToggleMinimize={onToggleMinimize}
            onClose={onClose}
            onToggleFavorite={handleToggleFavorite}
          />
        </div>

        <Collapse
          in={!minimized}
          timeout={300}
          sx={{
            flex: !minimized ? 1 : undefined,
            display: !minimized ? "flex" : undefined,
            flexDirection: "column",
            overflow: "hidden",
            "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            },
          }}
        >
          {activity && (
            <ActivityInfoBar
              activity={activity}
              onAssignClick={() => setShowAssignDialog(true)}
              onMoveClick={() => setShowMoveDialog(true)}
            />
          )}

          {activity && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <RescheduleCard activity={activity} />
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              bgcolor: "grey.50",
              p: 3,
            }}
          >
            {isLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress size={48} />
                  <Typography variant="body2" color="text.secondary">
                    Laddar aktivitet...
                  </Typography>
                </Stack>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <Typography variant="body1">
                  Kunde inte ladda aktiviteten. Försök igen senare.
                </Typography>
              </Alert>
            )}
   {activity && !minimized && (
                      <CustomerCallButton customerId={activity.Customer?.Id} />
                    )}
            {activity && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {shouldShowCalendarContent && (
                  <Box>
                    {isCalendarEventNotFound ? (
                      <CalendarEventError
                        activityId={activity.Id}
                        activityName={activity.Name}
                      />
                    ) : (
                      <MeetingCard
                        calendarEvent={calendarEvent}
                        isLoading={isCalendarLoading}
                        error={calendarError}
                      />
                    )}
                 
                  </Box>
                )}

                <CustomerInfoCard
                  activity={activity}
                  onCustomerClick={handleCustomerClick}
                  onUpdateDescription={handleUpdateDescription}
                />
              </Box>
            )}
          </Box>

          <ActivityPanelFooter
            activity={activity}
            onFinishClick={handleFinishClicked}
          />
        </Collapse>
      </Paper>

      {/* Sub-dialogs */}
      {activity && (
        <>
          <CompleteActionDialog
            open={showCompleteDialog}
            onCancel={() => setShowCompleteDialog(false)}
            onClose={() => setShowCompleteDialog(false)}
            customerNo={activity.Customer.Id}
            onComplete={handleCompleted}
            onCompleteWithNextAction={handleCompleteWithNextAction}
            workItemNo={activity.Id}
          />

          <VisualActionDialog
            open={showNextActionDialog}
            onClose={handleNextActionClose}
            customerNo={activity.Customer.Id}
            customerName={activity.Customer.Name}
            workId={activity.WorkId ?? undefined}
          />

          <MoveWorkItemDialog
            open={showMoveDialog}
            onClose={() => setShowMoveDialog(false)}
            onComplete={() => setShowMoveDialog(false)}
            workItemNo={activity.Id}
          />

          <AssignWorkItemDialog
            open={showAssignDialog}
            onClose={() => setShowAssignDialog(false)}
            workItem={activity}
          />
        </>
      )}
    </>
  );
};

export default ActivityPanel;
